import { useState, useRef } from "react"; 
import { Marker, Popup, useMapEvents, useMap } from "react-leaflet";
import Toast from "../Toast";
import isWithinStudyArea from "../../constants/studyArea";
import SoilPopupContent from "./SoilPopupContent";
import usePlaceName from "../../hooks/usePlaceName";
import useSoilData from "../../hooks/useSoilData";
import "../../styles/mapStyles/Popup.css";

function ClickMarker() {
  // States
  const [toast, setToast] = useState(null);
  const [clickedPin, setClickedPin] = useState(null); // holds the coordinates of the pin dropped by the user's click
  const [disableGetInfo, setDisableGetInfo] = useState(false); // used to hide the "Get Soil Info" button after it's clicked once to prevent multiple fetches for the same location

  const lastFetchedLocation = useRef(null); // Stores the last location we fetched soil data for

  // Gets direct access to the map instance so we can change the cursor style
  const map = useMap();

  // Get the human-readable place name for the user's coordinates using a custom hook that uses TanStack Query for caching and state management.
  const { placeName } = usePlaceName(clickedPin);

  // Custom hook using TanStack Query to fetch soil data and recommendations from Supabase when the user clicks "Get Soil Info"
  const { 
    fetchSoilData, 
    soilData, 
    recommendation, 
    isExactMatch,
    confidenceScore,        
    confidenceInterpretation, 
    pointsUsed,            
    isInterpolated,        
    isLoadingSoilData, 
    soilDataError, 
    reset 
  } = useSoilData();

  // Shows a toast message long enough for users to read it comfortably
  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(null), 8000);
  };

  // Wipes all soil state when a new pin is dropped
  const resetSoilState = () => {
    reset(); // TanStack Query reset — clears soilData, error, recommendation
    setDisableGetInfo(false); // re-enables the "Get Soil Info" button
    lastFetchedLocation.current = null; // clear the last fetched location since we're dropping a new pin and any previous data is now irrelevant
  };

  // useMapEvents listens for things happening on the map — in this case, mouse movements and clicks
  useMapEvents({
    // Fires constantly as the user moves their mouse over the map
    mousemove(e) {
      // Gets current mouse position
      const { lat, lng } = e.latlng;
      // Change the cursor to a pointer inside the boundary and a "not-allowed" symbol outside it
      map.getContainer().style.cursor = isWithinStudyArea(lat, lng) ? "pointer" : "not-allowed";
    },

    // Fires when the user clicks anywhere on the map
    click(e) {
      // Get the exact coordinates of the click
      const { lat, lng } = e.latlng;

      // Checks if the click is within the study area boundary — if not, show a warning toast and don't drop a pin
      if (!isWithinStudyArea(lat, lng)) {
        showToast("Please click within the Kaduna/Kano study area");
        return;
      }

      // Before dropping the new pin, wipe everything clean, this ensures the new pin doesn't show data from the previous pin
      resetSoilState();

      setClickedPin([lat, lng]); // Drop a pin at the clicked location
    },
  });

  // Fetches soil data for the clicked location and generates recommendations based on that data.
  const handleGetSoilInfo = async () => {
    if (!clickedPin) return;
    const [lat, lng] = clickedPin;

    // Skip if we already have data for this exact location
    if (lastFetchedLocation.current) {
      const [lastLat, lastLng] = lastFetchedLocation.current;
      const isSameLocation =
        Math.abs(lat - lastLat) <= 0.01 &&
        Math.abs(lng - lastLng) <= 0.01;

      // Same location, data already exists — no need to fetch again
      if (isSameLocation && soilData) return;
    }

    try {
      await fetchSoilData({ lat, lng });
    } catch (err) {
      return;
    }

    // Save the location we just successfully fetched data for
    lastFetchedLocation.current = [lat, lng];
    setDisableGetInfo(true);
  };

  return (
    <>
      {/* Toast notification */}
      {toast && <Toast message={toast} />}

      {/* Marker — only renders if a valid point was clicked */}
      {clickedPin && (
        <Marker position={clickedPin}>
          <Popup
            maxWidth={300}
            autoPan={true}
            keepInView={true}
            autoPanPaddingTopLeft={[20, 80]}
            autoPanPaddingBottomRight={[20, 20]}
          >
            <div className="popup-container">
              <div className="popup-header">
                {/* Displayes the location name and coordinates of the clicked location */}
                <strong className="popup-title">📍 {placeName}</strong>
                <p className="coordinates">
                  {clickedPin[0].toFixed(5)}°N, {clickedPin[1].toFixed(5)}°E
                </p>

                {/* Divider line */}
                <hr className="divider" />
              </div>

              <div className="popup-scroll-body">
                {/* Shared popup body — handles buttons, loading, errors, results */}
                <SoilPopupContent
                  placeName={placeName}
                  coordinates={clickedPin}
                  soilData={soilData}
                  recommendation={recommendation}
                  loading={isLoadingSoilData}
                  error={soilDataError}
                  disableGetInfo={disableGetInfo}
                  onGetSoilInfo={handleGetSoilInfo}
                  isClickMarker={true} // only ClickMarker passes this prop, used to conditionally render the "Remove Marker" button inside SoilPopupContent
                  onRemoveMarker={() => setClickedPin(null)} // only ClickMarker passes this
                  isExactMatch={isExactMatch}
                  confidenceScore={confidenceScore}
                  confidenceInterpretation={confidenceInterpretation}
                  pointsUsed={pointsUsed}
                  isInterpolated={isInterpolated}
                />
              </div>
            </div>
          </Popup>
        </Marker>
      )}
    </>
  );
}

export default ClickMarker;
